import 'react-calendar-timeline/lib/Timeline.css'

import { dayjs } from '@2wunder/klarx-tool'
import { Col, Empty, Grid, Row } from 'antd'
import { themeColor } from 'app/constants/Global'
import { ReactNode, useState } from 'react'
import * as React from 'react'
import ReactTimeline, {
  DateHeader,
  ReactCalendarGroupRendererProps,
  ReactCalendarItemRendererProps,
  ReactCalendarTimelineProps,
  SidebarHeader,
  TimelineGroupBase,
  TimelineHeaders,
  TimelineItemBase,
  TodayMarker
} from 'react-calendar-timeline'

import Navigation from './Navigation'

interface Props<TItem, TGroup> extends ReactCalendarTimelineProps {
  items: Array<TItem & TimelineItemBase<number>>
  groups: Array<TGroup & TimelineGroupBase>
  sidebarHeader?: React.ReactNode
  groupRenderer?: (
    props: ReactCalendarGroupRendererProps<TimelineGroupBase & TGroup>
  ) => React.ReactNode
  itemRenderer?: (
    props: ReactCalendarItemRendererProps<TItem & TimelineItemBase<number>>
  ) => React.ReactNode
  filters?: ReactNode
}

export type TimeOptions = {
  start?: number
  end?: number
  unit: 'week' | 'month'
}

const Timeline = <TItem, TGroup>({
  items,
  groups,
  sidebarHeader = null,
  itemRenderer,
  groupRenderer,
  filters
}: Props<TItem, TGroup>) => {
  const screens = Grid.useBreakpoint()
  const [timeOptions, setTimeOptions] = useState<TimeOptions>({
    unit: 'month',
    start: dayjs().add(-2, 'weeks').valueOf(),
    end: dayjs().add(2, 'weeks').valueOf()
  })

  const onTimeChange = (start: number, end: number) => {
    setTimeOptions({
      start: start,
      end: end,
      unit: timeOptions.unit
    })
  }

  const getLabelFormat = (values: [Dayjs, Dayjs], unit: string, width: number) => {
    if (unit === 'day') {
      return width > 40 ? values[0].format('dd DD') : values[0].format('DD')
    } else {
      return values[0].format('MMM')
    }
  }

  return (
    <Row gutter={[0, 12]}>
      <Col span={24}>
        <Navigation timeOptions={timeOptions} setTimeOptions={setTimeOptions} filters={filters} />
      </Col>
      <Col span={24}>
        <ReactTimeline<TItem & TimelineItemBase<number>, TGroup & TimelineGroupBase>
          groups={groups}
          maxZoom={604800000}
          minZoom={2592000000}
          items={items}
          traditionalZoom={false}
          groupRenderer={groupRenderer}
          itemRenderer={itemRenderer}
          lineHeight={50}
          sidebarWidth={screens.md ? 135 : 0}
          canResize={false}
          canMove={false}
          onTimeChange={onTimeChange}
          visibleTimeStart={timeOptions.start}
          visibleTimeEnd={timeOptions.end}>
          <TimelineHeaders>
            <If condition={screens.md}>
              <SidebarHeader>
                {({ getRootProps }) => <div {...getRootProps()}>{sidebarHeader}</div>}
              </SidebarHeader>
            </If>
            <DateHeader labelFormat={getLabelFormat} />
          </TimelineHeaders>
          <TodayMarker date={new Date()}>
            {({ styles }) => (
              <div style={{ ...styles, backgroundColor: themeColor, opacity: 0.7 }} />
            )}
          </TodayMarker>
        </ReactTimeline>
      </Col>
      <Col span={24}>
        <If condition={items.length === 0}>
          <Empty />
        </If>
      </Col>
    </Row>
  )
}

export default Timeline
